import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { CheckUserEligibilityResponseBody, SignupFormData } from 'features/signUp/types';
import {
  useLazyGetUserEligibilityQuery,
  useGetNonSSOUserEligibilityMutation,
} from 'services/signupService';
import { getYearsElapsed, isFetchBaseQueryError } from 'utils';
import { UseFormReturn } from 'react-hook-form';
import { useAppDispatch } from 'store/store';
import { setSignUpState } from 'features/signUp/signUpSlice';
import { MINIMUM_USER_AGE, SIGNUP_ERROR_MESSAGE } from 'features/signUp/constants';
import { safeFormatToNational } from 'utils/phoneNumberUtils/phoneNumberUtils';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { SignupError } from '../useSignupError/SignupError';
import { useSignupData } from '../useSignupData';

export const useVerifyEligibilityStep = (
  useFormReturn: UseFormReturn<SignupFormData, any, undefined>,
) => {
  const {
    eligibilityCheckMethod: eligibilityVerificationMethod,
    isUserInvitedCaregiver,
    userType,
    isHospitalSsoEnabled,
  } = useSignupData();
  const { isEnabled } = useFeatureFlags();
  const isNonSSOEligibilityChangeEnabled = isEnabled(
    FeatureFlagKeys.ELIGIBILITY_API_NON_SSO_CHANGES,
  );
  const [verifyUserEligiblity, { isLoading: isSubmittingV1 }] = useLazyGetUserEligibilityQuery();
  const [verifyUserEligibilityV2, { isLoading: isSubmittingV2 }] =
    useGetNonSSOUserEligibilityMutation();
  const isSubmitting = isSubmittingV1 || isSubmittingV2;
  const { getValues, setValue } = useFormReturn;
  const appDispatch = useAppDispatch();

  const transformRequestError = (error: FetchBaseQueryError | SerializedError): SignupError => {
    if (isFetchBaseQueryError(error)) {
      if (error.status === 'FETCH_ERROR') {
        return new SignupError(SIGNUP_ERROR_MESSAGE.NoNetworkVerifyEligibility, 'NoNetwork');
      }

      if (error.status === 404) {
        return new SignupError(SIGNUP_ERROR_MESSAGE.IneligibleUser, 'IneligibleUser');
      }
      if (error.status === 422) {
        return new SignupError(SIGNUP_ERROR_MESSAGE.InvalidAccessCode, 'InvalidAccessCode');
      }
    }

    return new SignupError(SIGNUP_ERROR_MESSAGE.GenericVerifyEligibility, 'Generic');
  };

  const handleSuccess = (data: CheckUserEligibilityResponseBody) => {
    setValue('memberFirstName', data.first_name);
    setValue('memberLastName', data.last_name);
    setValue('memberDateOfBirth', data.dob);
    setValue('memberGender', data.gender);
    setValue('memberPhoneNumber', safeFormatToNational(data.phone_number));
    setValue('memberEmail', data.email);

    if (isNonSSOEligibilityChangeEnabled) {
      setValue('memberId', data.member_id);
    }

    if (data.caregiver && isUserInvitedCaregiver) {
      setValue('caregiverFirstName', data.caregiver.first_name);
      setValue('caregiverLastName', data.caregiver.last_name);
      setValue('caregiverPhoneNumber', safeFormatToNational(data.caregiver.phone_number));
    }

    appDispatch(
      setSignUpState({
        wellframeUserId: data.id,
        ...(isNonSSOEligibilityChangeEnabled && { memberId: data.member_id }),
        activeUserToken: data.active_user_token,
        isUserApproved: data.approved_status,
        userSignupStatus: data.status,
      }),
    );
  };

  const submit = async () => {
    const memberDateOfBirth = getValues('memberDateOfBirth');
    const userAge = getYearsElapsed(new Date(memberDateOfBirth), new Date());
    const isUserAgeRestricted = userType === 'member' && userAge < MINIMUM_USER_AGE;

    if (isUserAgeRestricted) {
      throw new SignupError(SIGNUP_ERROR_MESSAGE.UnderageUser, 'UnderageUser');
    }

    try {
      const requestParams = {
        member_id:
          eligibilityVerificationMethod === 'memberId'
            ? getValues('memberId')
            : getValues('memberLastName'),
        code: getValues('accessCode'),
        dob: memberDateOfBirth,
      };

      const { data, error } = isNonSSOEligibilityChangeEnabled
        ? await verifyUserEligibilityV2(requestParams)
        : await verifyUserEligiblity(requestParams);

      if (error) {
        throw transformRequestError(error);
      }

      if (!data) {
        throw new SignupError(SIGNUP_ERROR_MESSAGE.GenericVerifyEligibility, 'Generic');
      }

      handleSuccess(data);
    } catch (error) {
      if (error instanceof SignupError) {
        throw error;
      }

      throw new SignupError(SIGNUP_ERROR_MESSAGE.GenericVerifyEligibility, 'Generic');
    }
  };

  return { isSubmitting, submit };
};
